/**
 * Normalizes AttendancePolicyComponent / shift form fields for the calculation engine.
 */

export const PENALTY_TYPES = ["none", "minutes_lop", "half_day", "full_day", "lop", "absent"];

/** Half-day composites (first half / second half) — no separate HD code. */
export const HALF_DAY_LOP_PR = "LOP/PR";
export const HALF_DAY_PR_LOP = "PR/LOP";

/** Map UI latePenaltyType → engine action + day status impact */
export function normalizePenaltyType(raw) {
  const t = String(raw || "none").toLowerCase();
  if (PENALTY_TYPES.includes(t)) return t;
  if (t === "lop") return "full_day";
  return "none";
}

/** Sync legacy latePenaltyAction from UI latePenaltyType on save/load */
export function normalizePolicyRecord(policy) {
  if (!policy || typeof policy !== "object") return policy;
  const lateType = normalizePenaltyType(
    policy.latePenaltyType || policy.latePenaltyAction
  );
  const earlyType = normalizePenaltyType(
    policy.earlyDeparturePenaltyType || policy.earlyExitPenaltyAction
  );
  return {
    ...policy,
    latePenaltyType: lateType,
    earlyDeparturePenaltyType: earlyType,
    latePenaltyAction:
      lateType === "half_day" || lateType === "full_day" || lateType === "minutes_lop"
        ? "lop"
        : lateType === "absent"
        ? "absent"
        : policy.latePenaltyAction || "none",
    earlyExitPenaltyAction:
      earlyType === "half_day" || earlyType === "full_day" || earlyType === "minutes_lop"
        ? "lop"
        : earlyType === "absent"
        ? "absent"
        : policy.earlyExitPenaltyAction || "none",
  };
}

export function normalizeShiftRecord(shift) {
  if (!shift || typeof shift !== "object") return shift;
  return {
    ...shift,
    shiftType: shift.shiftType || (shift.flexiEnabled ? "flexi" : "fixed"),
    latePenaltyType: normalizePenaltyType(shift.latePenaltyType),
    earlyDeparturePenaltyType: normalizePenaltyType(shift.earlyDeparturePenaltyType),
  };
}

/**
 * Merged timing + hours config: shift overrides policy when shift.graceRulesEnabled.
 */
export function getEffectiveTimingConfig(policy = {}, shift = null) {
  const p = normalizePolicyRecord(policy || {});
  const s = shift ? normalizeShiftRecord(shift) : null;
  const shiftGraceOn = s?.graceRulesEnabled !== false;

  const pick = (shiftKey, policyKey, fallback) => {
    if (shiftGraceOn && s && s[shiftKey] != null && s[shiftKey] !== "") {
      return s[shiftKey];
    }
    if (p[policyKey] != null && p[policyKey] !== "") return p[policyKey];
    return fallback;
  };

  return {
    graceRulesEnabled:
      p.graceRulesEnabled !== false && (s?.graceRulesEnabled !== false),
    graceCheckInMinutes: Number(pick("graceCheckInMinutes", "graceCheckInMinutes", 15)),
    graceCheckOutMinutes: Number(pick("graceCheckOutMinutes", "graceCheckOutMinutes", 15)),
    considerLateAfterMinutes: Number(
      pick("considerLateAfterMinutes", "considerLateAfterMinutes", 15)
    ),
    considerEarlyBeforeMinutes: Number(
      pick("considerEarlyBeforeMinutes", "considerEarlyBeforeMinutes", 30)
    ),
    earlyLoginBufferMinutes: Number(pick("earlyLoginBufferMinutes", "earlyLoginBufferMinutes", 0)),
    lateLogoutBufferMinutes: Number(pick("lateLogoutBufferMinutes", "lateLogoutBufferMinutes", 0)),
    graceOccurrenceWindow: pick("graceOccurrenceWindow", "graceOccurrenceWindow", "monthly"),
    lateGraceAllowedOccurrences: Number(
      pick("lateGraceAllowedOccurrences", "lateGraceAllowedOccurrences", 0)
    ),
    earlyGraceAllowedOccurrences: Number(
      pick("earlyGraceAllowedOccurrences", "earlyGraceAllowedOccurrences", 0)
    ),
    lateAllowedOccurrencesPerMonth: Number(
      p.lateAllowedOccurrencesPerMonth ?? p.lateGraceAllowedOccurrences ?? 0
    ),
    earlyExitAllowedOccurrencesPerMonth: Number(
      p.earlyExitAllowedOccurrencesPerMonth ?? p.earlyGraceAllowedOccurrences ?? 0
    ),
    latePenaltyType: normalizePenaltyType(
      p.latePenaltyType || s?.latePenaltyType || p.latePenaltyAction
    ),
    latePenaltyMinutes: Number(p.latePenaltyMinutes ?? s?.latePenaltyMinutes ?? 0),
    earlyDeparturePenaltyType: normalizePenaltyType(
      p.earlyDeparturePenaltyType ||
        s?.earlyDeparturePenaltyType ||
        p.earlyExitPenaltyAction
    ),
    earlyDeparturePenaltyMinutes: Number(
      p.earlyDeparturePenaltyMinutes ?? s?.earlyDeparturePenaltyMinutes ?? 0
    ),
    fullDayHours: Number(pick("fullDayHours", "fullDayHours", 8)),
    halfDayHours: Number(pick("halfDayHours", "halfDayHours", 4)),
    fullDayIfHoursAtLeast: Number(
      pick("fullDayIfHoursAtLeast", "fullDayIfHoursAtLeast", 8)
    ),
    halfDayIfHoursLessThan: Number(
      pick("halfDayIfHoursLessThan", "halfDayIfHoursLessThan", 4)
    ),
    halfDayMarkIfHoursBelow: Number(
      p.halfDayMarkIfHoursBelow ?? pick("halfDayIfHoursLessThan", "halfDayIfHoursLessThan", 4)
    ),
    absentMarkIfHoursBelow: Number(p.absentMarkIfHoursBelow ?? 2),
    autoAbsentMarkingEnabled: p.autoAbsentMarkingEnabled !== false,
    autoHalfDayMarkingEnabled: p.autoHalfDayMarkingEnabled !== false,
    defaultMarkingForMissingPunch: p.defaultMarkingForMissingPunch || "half_day",
    breakDeductionMethod: p.breakDeductionMethod || "fixed",
    breakFixedMinutes: Number(p.breakFixedMinutes ?? 0),
    workingHoursRoundingUnit: Number(p.workingHoursRoundingUnit) || 0,
    workingHoursRoundingDirection: p.workingHoursRoundingDirection || "nearest",
    allowShiftOverride: !!(s?.allowShiftOverride),
    shiftOverrideRequiresApproval: !!(s?.shiftOverrideRequiresApproval),
    checkInBiometric: p.checkInBiometric !== false,
    checkInMobile: p.checkInMobile !== false,
    checkInWeb: p.checkInWeb !== false,
    regularizationAllowed: p.regularizationAllowed !== false,
    shortLeaveMaxMinutes: Number(
      p.shortLeaveMaxMinutes ?? p.considerLateAfterMinutes ?? 60
    ),
    shiftType: s?.shiftType || "fixed",
    flexiBandwidthStart: s?.flexiBandwidthStart,
    flexiBandwidthEnd: s?.flexiBandwidthEnd,
    coreHoursStart: s?.coreHoursStart,
    coreHoursEnd: s?.coreHoursEnd,
    crossDayShift: !!(s?.crossDayShift),
  };
}

/**
 * @param {"late"|"early"} context — late/missing → LOP/PR; early exit → PR/LOP
 */
export function penaltyToDayStatus(penaltyType, context = "late") {
  const t = normalizePenaltyType(penaltyType);
  if (t === "full_day" || t === "absent" || t === "lop") return "LOP";
  if (t === "half_day") {
    return context === "early" ? HALF_DAY_PR_LOP : HALF_DAY_LOP_PR;
  }
  return null;
}

export function missingPunchMarkToStatus(mark) {
  const m = String(mark || "half_day").toLowerCase();
  if (m === "present") return "PR";
  if (m === "half_day") return HALF_DAY_LOP_PR;
  return "LOP";
}
